import { useEffect, useState } from "react";
import PacienteService from "../../../services/PacienteService";
import IPaciente from "../../../types/Paciente";
import { useNavigate } from "react-router-dom";
import IAlert from "../../../types/Alert";
import { IBonoPaciente } from "../../../types/BonoPaciente";
import * as constantes from "../../../common/Constantes";
import ILog from "../../../types/Log";
import LogService from "../../../services/LogService";
import { Alert, Box, Chip, CircularProgress, Container, createTheme, Grid, Paper, Snackbar, Tooltip, Typography } from "@mui/material";
import moment from "moment";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DetalleBonoPaciente from "../../facturacion/facturarBono/DetalleBonoPaciente";
import ConfigUtils from "../../../utils/Config";

type BonosPacienteProps = {
    paciente: IPaciente;
}

const BonosPaciente = ({ paciente }: BonosPacienteProps) => {
    const theme = createTheme();
    const history = useNavigate();
    const [alert, setAlert] = useState<IAlert>({ open: false, severity: 'info', msg: '' });
    const [loading, setLoading] = useState(true);
    const [bonosPaciente, setBonosPaciente] = useState<IBonoPaciente[]>([]);
    const [selectedBonoPaciente, setSelectedBonoPaciente] = useState<IBonoPaciente>();
    const [modalDetalle, setModalDetalle] = useState(false);
    const servicios = ConfigUtils.getServicios().filter(s => !s.recurrente && s.activo);

    useEffect(() => {
      load();
    }, []);

    const load = async () => {
      try {
        const response = await PacienteService.getBonos(paciente._id);
        setBonosPaciente(ordenarBonos(response.data || []));
      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.clear();
          history(constantes.R_HOME);
        } else {
          const error: ILog = {
            method: "BonosPaciente.Component.load",
            level: "error",
            message: e.message,
            meta: e.response,
          };
          LogService.logError(error);
          setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
        }
      } finally {
        setLoading(false);
      }
    };

    const handleCloseAlert = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setAlert({ open: false, severity: 'info', msg: '' });
    };

    const openDetalle = (bono: IBonoPaciente) => {
      setSelectedBonoPaciente(bono);
      setModalDetalle(true);
    };

    const closeDetalle = () => {
      setSelectedBonoPaciente(undefined);
      setModalDetalle(false);
    };

    const ordenarBonos = (bonos: IBonoPaciente[]) => {  
      const ahora = moment();

      return [...bonos].sort((a, b) => {
        const restantesA = a.cantidad - a.usado;
        const restantesB = b.cantidad - b.usado;
      
        const cadA = moment(a.caducidad);
        const cadB = moment(b.caducidad);
      
        const caducadoA = cadA.isBefore(ahora, "day");
        const caducadoB = cadB.isBefore(ahora, "day");
      
        const agotadoA = restantesA <= 0;
        const agotadoB = restantesB <= 0;
      
        // 1️⃣ utilizables primero
        const usableA = !caducadoA && !agotadoA;
        const usableB = !caducadoB && !agotadoB;
      
        if (usableA !== usableB) return usableA ? -1 : 1;
      
        // 2️⃣ dentro de utilizables: propios primero
        if (usableA && usableB) {
          const propioA = a.pacienteId === paciente._id;
          const propioB = b.pacienteId === paciente._id;
        
          if (propioA !== propioB) return propioA ? -1 : 1;
        
          // 3️⃣ caducidad más cercana primero
          return cadA.diff(cadB);
        }
      
        // 4️⃣ agotados antes que caducados
        if (agotadoA !== agotadoB) return agotadoA ? -1 : 1;
      
        // 5️⃣ dentro de cada grupo ordenar por caducidad
        return cadA.diff(cadB);
      });
    };

    const getEstadoBono = (bono: IBonoPaciente) => {
      const restantes = bono.cantidad - bono.usado;

      if (restantes <= 0)
        return { label: "Agotado", color: "error" };

      const hoy = moment().startOf("day");
      const caducidad = bono.caducidad ? moment(bono.caducidad).startOf("day") : null;

      if (caducidad && caducidad.isBefore(hoy))
        return { label: "Caducado", color: "default" };

      if (restantes <= 2)
        return { label: "Pocas sesiones", color: "warning" };

      return { label: "Activo", color: "success" };
    };

    try {
        return (
            <>
                <Grid item md={12} xs={12}>
                    <Container maxWidth="md">
                        <Paper elevation={3} sx={{ marginTop: theme.spacing(3), padding: theme.spacing(3), display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="h6" gutterBottom style={{ fontWeight: 'bold', marginLeft: '10px' }} >BONOS</Typography>
                            {loading ? 
                            <Grid container>
                                <CircularProgress style={{ margin: '10px auto' }} />
                            </Grid> : 
                                <Box mt={2}>
                                  {bonosPaciente.length === 0 && <Typography>No se han encontrado bonos</Typography>}
                                
                                  {bonosPaciente.map((bono) => {
                                    const restantes = bono.cantidad - bono.usado;
                                    const estado = getEstadoBono(bono);
                                
                                    return (
                                      <Box
                                        key={bono._id}
                                        sx={{
                                          p: 1.5,
                                          mb: 1,
                                          border: "1px solid #eee",
                                          borderRadius: 2,
                                          cursor: "pointer",
                                          "&:hover": { backgroundColor: "#fafafa" },
                                        }}
                                        onClick={() => openDetalle(bono)}
                                      >
                                        <Box display="flex" alignItems="center" gap={1}>
                                          <Typography fontWeight={600}>
                                            {bono.nombre}
                                          </Typography>
                                      
                                          {bono.pacienteId !== paciente._id && (
                                            <Tooltip title="Este bono ha sido compartido con este paciente">
                                              <InfoOutlinedIcon fontSize="small" color="action" />
                                            </Tooltip>
                                          )}
                                        </Box>
                                      
                                        <Typography variant="body2" color="text.secondary">
                                          Sesiones: {restantes}/{bono.cantidad}
                                        </Typography>
                                      
                                        <Typography variant="body2" color="text.secondary">
                                          Caduca: {moment(bono.caducidad).format("DD/MM/YYYY")}
                                        </Typography>
                                      
                                        <Chip
                                          size="small"
                                          label={estado.label}
                                          color={estado.color as any}
                                          sx={{ mt: 1 }}
                                        />
                                      </Box>
                                    );
                                  })}
                                </Box>
                            }
                        </Paper>
                    </Container>
                </Grid>

                {/* DETALLE BONO PACIENTE */}
                {modalDetalle && 
                  <DetalleBonoPaciente
                    bonoPaciente={selectedBonoPaciente!}
                    pacientes={[paciente]}
                    servicios={servicios}
                    open={modalDetalle}
                    onClose={closeDetalle}
                    onAnular={(_id) => {
                      setBonosPaciente(bonosPaciente.filter(b => b._id !== _id));
                      setAlert({ open: true, severity: "success", msg: "Bono anulado correctamente" });
                    }}
                  />
                }

                <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
                    <Alert variant="filled" onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>{alert.msg}</Alert>
                </Snackbar>
            </>
        );
    } catch (e: any) {
        const error: ILog = { method: 'BonosPaciente.Component', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return (
            <Alert severity="error">{e.message}</Alert>
        );
    }
};

export default BonosPaciente;